import React, { useState, useEffect } from 'react';
import { MdGroup, MdStorage, MdLock, MdExplicit, MdAssessment, MdOpenInNew } from "react-icons/md";

import { Page } from 'components';
import { Button, Card, Col, Row, Spinner, Container, Badge } from 'react-bootstrap'
import { HorizontalBar } from 'react-chartjs-2';

import CertificadoDash from './components/CertificadoDash';
import Reload from '../../components/Reload/Reload';
import Header from 'components/Header/Header';
import ApiMServer from 'services/ApiMServer';
import ApiGServer from 'services/ApiGServer';
import ApiGSenha from 'services/ApiGSenha';
import Api from 'services/Api';

// Cores da marca Motomco com paleta expandida para gráficos
const brandColors = {
  lightGreen: 'var(--brand-light-green, #8fad15)',
  darkGreen: 'var(--brand-dark-green, #1c4220)',
  lighterGreen: 'var(--brand-lighter-green, rgba(143, 173, 21, 0.7))',
  darkerGreen: 'var(--brand-darker-green, rgba(28, 66, 32, 0.7))',
  // Paleta expandida para gráficos
  chartColors: {
    simt: {
      light: '#3ab34a',
      dark: '#2a7a37'
    },
    bimt: {
      light: '#8fad15',
      dark: '#5d7110'
    },
    cpmt: {
      light: '#24a79d',
      dark: '#1a756e'
    },
    eemt: {
      light: '#0c84bf',
      dark: '#095a83'
    },
    esmr: {
      light: '#9c4eca',
      dark: '#6c368d'
    }
  }
}

// Configuração do gráfico adaptada para tema escuro/claro
const getChartOptions = () => {
  const isDarkTheme = document.body.classList.contains('dark-theme');
  
  return {
    scales: {
      y: [{
        ticks: {
          beginAtZero: true,
          fontColor: isDarkTheme ? '#adb5bd' : '#666',
          fontStyle: 'bold',
          precision: 0
        },
        gridLines: {
          display: false
        },
        maxBarThickness: 30
      }],
      x: [{
        ticks: {
          beginAtZero: true,
          fontColor: isDarkTheme ? '#adb5bd' : '#666',
          padding: 10,
          maxRotation: 0
        },
        gridLines: {
          color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          zeroLineColor: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
        }
      }]
    },
    legend: {
      display: false
    },
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      titleFontColor: isDarkTheme ? '#f8f9fa' : '#333',
      bodyFontColor: isDarkTheme ? '#adb5bd' : '#666',
      borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      displayColors: true,
      cornerRadius: 6,
      caretSize: 6,
      titleFontStyle: 'bold',
      callbacks: {
        title: (items) => `Versão: ${items[0].label}`,
        label: (item) => `Quantidade: ${item.raw || item.value}`
      }
    }
  };
};

// Função para criar gradientes para as barras do gráfico (melhora visual)
const createBarGradient = (ctx, colors, isDark) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, isDark ? colors.dark : colors.light);
  gradient.addColorStop(1, isDark ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

const Home = () => {
  const user = JSON.parse(localStorage.getItem('userData'));
  const [pagePermission, setPagePermission] = useState(0);
  const [n_users, setNusers] = useState();
  const [n_measurements, setNMeasurements] = useState();
  const [n_unblocks, setNUnblocks] = useState();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(getChartOptions());

  const [equipmentMetrics, setEquipmentMetrics] = useState({
    simt: { n: 0, softVesions: {}, labels: [], data: [] },
    bimt: { n: 0, softVesions: {}, labels: [], data: [] },
    cpmt: { n: 0, softVesions: {}, labels: [], data: [] },
    eemt: { n: 0, softVesions: {}, labels: [], data: [] },
    esmr: { n: 0, softVesions: {}, labels: [], data: [] },
  });
  
  // Observa mudanças no tema para atualizar as opções do gráfico
  useEffect(() => {
    const handleThemeChange = () => {
      setOptions(getChartOptions());
    };

    // Observer para alterações na classe do body (tema claro/escuro)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    // Limpar observer ao desmontar
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (user?.permission?.home?.read !== true) {
      setPagePermission(1);

      let keys = Object.keys(user?.permission);
      for (let i = 0; i < keys.length; i++) {
        let page = keys[i];
        if (user?.permission[page]?.read) {
          window.location.replace('/' + page);
          return;
        }
      }
    } else {
      setPagePermission(2);
    }
  }, [user]);

  useEffect(() => {
    const handleGetUser = async () => {
      const storedUser = localStorage.getItem('userData');
      if (!storedUser) return;

      try {
        const { _id } = JSON.parse(storedUser);
        const { data: newUserData } = await ApiMServer.get(`/users/${_id}`);

        if (newUserData) {
          const updatedUser = {
            ...JSON.parse(storedUser),
            ...newUserData,
          };
          localStorage.setItem('userData', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
      }
    };
    handleGetUser()

    const getAll = async () => {
      setLoading(true)

      try {
        let unlockcount = await ApiGSenha.get('/log/unlockcount');
        setNUnblocks(unlockcount?.data?.count);

        let dashboard = await ApiMServer.get('/dashboard');
        setNusers(dashboard?.data?.n_users);

        let measurements = await Api.get('/measurements/count');
        setNMeasurements(measurements?.data?.n_measurements);

        let softwares = await ApiGServer.get('/equipment/metrics');
        const data = softwares.data;

        const sortVersionsDescending = (versions) => {
          return Object.entries(versions)
            .sort((a, b) => b[1] - a[1])
            .reduce((acc, [key, value]) => {
              acc[key] = value;
              return acc;
            }, {});
        };

        const sortedSimt = sortVersionsDescending(data.simt.softVesions);
        data.simt.labels = Object.keys(sortedSimt);
        data.simt.data = Object.values(sortedSimt);

        const sortedBimt = sortVersionsDescending(data.bimt.softVesions);
        data.bimt.labels = Object.keys(sortedBimt);
        data.bimt.data = Object.values(sortedBimt);

        const sortedCpmt = sortVersionsDescending(data.cpmt.softVesions);
        data.cpmt.labels = Object.keys(sortedCpmt);
        data.cpmt.data = Object.values(sortedCpmt);

        const sortedEemt = sortVersionsDescending(data.eemt.softVesions);
        data.eemt.labels = Object.keys(sortedEemt);
        data.eemt.data = Object.values(sortedEemt);

        const sortedEsmr = sortVersionsDescending(data.esmr.softVesions);
        data.esmr.labels = Object.keys(sortedEsmr);
        data.esmr.data = Object.values(sortedEsmr);

        setEquipmentMetrics(data);
        localStorage.setItem('equipMetrics', JSON.stringify(data));
        setLoading(false);

      } catch (error) {
        console.log(error.message);
        setLoading(false);
      }
    }
    getAll()
  }, []);

  const handlePlanilha = async (serial) => {
    try {
      window.open(`${process.env.REACT_APP_API_MOTOMCO}/dashboard/activelogsfilter?typeSerial=${serial}`, "_blank")
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <>
      {pagePermission === 0 ? (
        <div className="d-flex align-items-center justify-content-center" style={{height: '100vh'}}>
          <Spinner animation="border" style={{color: 'var(--brand-light-green)'}} size="lg" /> 
          <span className="ms-2">Carregando...</span>
        </div>
      ) : pagePermission === 1 ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{height: '100vh'}}>
          <div className="text-center p-4 dashboard-card" style={{maxWidth: '500px', borderRadius: '16px'}}>
            <MdLock size={48} style={{color: 'var(--brand-light-green)', marginBottom: '20px'}} />
            <h2>Acesso Restrito</h2>
            <p className="text-muted">Você não tem permissão para acessar o sistema. Entre em contato com o administrador.</p>
          </div>
        </div>
      ) : pagePermission === 2 ? (

        <Page className="p-4" title="DASHBOARD">
          <Header page={"DASHBOARD"} />

          {loading && <Reload />}
          
          <Container fluid className="px-0">
            {/* Estatísticas */}
            <Card className='mb-4 dashboard-card' style={{borderRadius: '16px'}}>
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-4 section-header" style={{color: 'var(--brand-dark-green)'}}>
                  Visão Geral
                </h4>
                <Row className='g-4'>
                  <Col className='px-3' xs={12} sm={6} md={6} lg={3}>
                    <div className="p-4 text-center h-100 stat-card" style={{borderRadius: '12px'}}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3 stat-card-icon" 
                          style={{width: '64px', height: '64px', borderRadius: '50%'}}>
                        <MdGroup size={32} style={{color: 'white'}} />
                      </div>
                      <h3 className='mb-0 fw-bold' style={{color: 'var(--brand-dark-green)'}}>
                        {n_users ? n_users : <Spinner animation="border" style={{color: 'var(--brand-light-green)'}} size="sm" />}
                      </h3>
                      <p className="text-muted mb-0">Usuários</p>
                    </div>
                  </Col>

                  {/* Outros cards de estatísticas com mesmas melhorias */}
                  <Col className='px-3' xs={12} sm={6} md={6} lg={3}>
                    <div className="p-4 text-center h-100 stat-card" style={{borderRadius: '12px'}}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3 stat-card-icon"
                          style={{width: '64px', height: '64px', borderRadius: '50%'}}>
                        <MdExplicit size={32} style={{color: 'white'}} />
                      </div>
                      <h3 className='mb-0 fw-bold' style={{color: 'var(--brand-dark-green)'}}>
                        {equipmentMetrics?.count !== undefined ? 
                          equipmentMetrics.count : 
                          <Spinner animation="border" style={{color: 'var(--brand-light-green)'}} size="sm" />
                        }
                      </h3>
                      <p className="text-muted mb-0">Equipamentos</p>
                    </div>
                  </Col>

                  <Col className='px-3' xs={12} sm={6} md={6} lg={3}>
                    <div className="p-4 text-center h-100 stat-card" style={{borderRadius: '12px'}}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3 stat-card-icon"
                          style={{width: '64px', height: '64px', borderRadius: '50%'}}>
                        <MdStorage size={32} style={{color: 'white'}} />
                      </div>
                      <h3 className='mb-0 fw-bold' style={{color: 'var(--brand-dark-green)'}}>
                        {n_measurements ? 
                          n_measurements : 
                          <Spinner animation="border" style={{color: 'var(--brand-light-green)'}} size="sm" />
                        }
                      </h3>
                      <p className="text-muted mb-0">Medidas Realizadas</p>
                    </div>
                  </Col>

                  <Col className='px-3' xs={12} sm={6} md={6} lg={3}>
                    <div className="p-4 text-center h-100 stat-card" style={{borderRadius: '12px'}}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3 stat-card-icon"
                          style={{width: '64px', height: '64px', borderRadius: '50%'}}>
                        <MdLock size={32} style={{color: 'white'}} />
                      </div>
                      <h3 className='mb-0 fw-bold' style={{color: 'var(--brand-dark-green)'}}>
                        {n_unblocks ? 
                          n_unblocks : 
                          <Spinner animation="border" style={{color: 'var(--brand-light-green)'}} size="sm" />
                        }
                      </h3>
                      <p className="text-muted mb-0">Ativações de Equipamentos</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Gráficos e conteúdo principal */}
            <Card className='mb-4 dashboard-card' style={{borderRadius: '16px'}}>
              <Card.Body className="p-4">
                <h4 className="mb-4 fw-bold section-header" style={{color: 'var(--brand-dark-green)'}}>Distribuição de Software por Equipamento</h4>
                
                <Row className="g-4 mb-3">
                  <Col xs={12} lg={6}>
                    <div className="mb-4 p-3 chart-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="fw-bold mb-0" style={{color: brandColors.darkGreen}}>SIMT</h5>
                          <Badge className="mt-1 badge-light" pill>
                            {equipmentMetrics.simt.n} equipamentos
                          </Badge>
                        </div>
                        <Button
                          className='px-3'
                          onClick={() => { handlePlanilha("SIMT") }}
                          disabled={user?.permission?.home?.write === true ? false : true}
                          variant="outline-success"
                          size='sm'
                          style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      </div>
                      <div style={{height: '350px', position: 'relative'}}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = document.body.classList.contains('dark-theme');
                            
                            return {
                              labels: equipmentMetrics.simt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.simt.data,
                                  backgroundColor: createBarGradient(ctx, brandColors.chartColors.simt, isDark),
                                  borderColor: isDark ? brandColors.chartColors.simt.dark : brandColors.chartColors.simt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? brandColors.chartColors.simt.dark : brandColors.chartColors.simt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 chart-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="fw-bold mb-0" style={{color: brandColors.darkGreen}}>CPMT</h5>
                          <Badge className="mt-1 badge-light" pill>
                            {equipmentMetrics.cpmt.n} equipamentos
                          </Badge>
                        </div>
                        <Button
                          className='px-3'
                          onClick={() => { handlePlanilha("CPMT") }}
                          disabled={user?.permission?.home?.write === true ? false : true}
                          variant="outline-success"
                          size='sm'
                          style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      </div>
                      <div style={{height: '350px', position: 'relative'}}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = document.body.classList.contains('dark-theme');
                            
                            return {
                              labels: equipmentMetrics.cpmt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.cpmt.data,
                                  backgroundColor: createBarGradient(ctx, brandColors.chartColors.cpmt, isDark),
                                  borderColor: isDark ? brandColors.chartColors.cpmt.dark : brandColors.chartColors.cpmt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? brandColors.chartColors.cpmt.dark : brandColors.chartColors.cpmt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} lg={6}>
                    <div className="mb-4 p-3 chart-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="fw-bold mb-0" style={{color: brandColors.darkGreen}}>BIMT</h5>
                          <Badge className="mt-1 badge-light" pill>
                            {equipmentMetrics.bimt.n} equipamentos
                          </Badge>
                        </div>
                        <Button
                          className='px-3'
                          onClick={() => { handlePlanilha("BIMT") }}
                          disabled={user?.permission?.home?.write === true ? false : true}
                          variant="outline-success"
                          size='sm'
                          style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      </div>
                      <div style={{height: '350px', position: 'relative'}}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = document.body.classList.contains('dark-theme');
                            
                            return {
                              labels: equipmentMetrics.bimt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.bimt.data,
                                  backgroundColor: createBarGradient(ctx, brandColors.chartColors.bimt, isDark),
                                  borderColor: isDark ? brandColors.chartColors.bimt.dark : brandColors.chartColors.bimt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? brandColors.chartColors.bimt.dark : brandColors.chartColors.bimt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 chart-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="fw-bold mb-0" style={{color: brandColors.darkGreen}}>EEMT</h5>
                          <Badge className="mt-1 badge-light" pill>
                            {equipmentMetrics.eemt.n} equipamentos
                          </Badge>
                        </div>
                        <Button
                          className='px-3'
                          onClick={() => { handlePlanilha("EEMT") }}
                          disabled={user?.permission?.home?.write === true ? false : true}
                          variant="outline-success"
                          size='sm'
                          style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      </div>
                      <div style={{height: '350px', position: 'relative'}}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = document.body.classList.contains('dark-theme');
                            
                            return {
                              labels: equipmentMetrics.eemt.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.eemt.data,
                                  backgroundColor: createBarGradient(ctx, brandColors.chartColors.eemt, isDark),
                                  borderColor: isDark ? brandColors.chartColors.eemt.dark : brandColors.chartColors.eemt.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? brandColors.chartColors.eemt.dark : brandColors.chartColors.eemt.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="g-4">
                  <Col xs={12} lg={6}>
                    <div className="p-3 chart-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="fw-bold mb-0" style={{color: brandColors.darkGreen}}>ESMR</h5>
                          <Badge className="mt-1 badge-light" pill>
                            {equipmentMetrics.esmr.n} equipamentos
                          </Badge>
                        </div>
                        <Button
                          className='px-3'
                          onClick={() => { handlePlanilha("ESMR") }}
                          disabled={user?.permission?.home?.write === true ? false : true}
                          variant="outline-success"
                          size='sm'
                          style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                        >
                          <MdAssessment className="me-1" /> Relatório
                        </Button>
                      </div>
                      <div style={{height: '350px', position: 'relative'}}>
                        <HorizontalBar
                          data={(canvas) => {
                            const ctx = canvas.getContext('2d');
                            const isDark = document.body.classList.contains('dark-theme');
                            
                            return {
                              labels: equipmentMetrics.esmr.labels,
                              datasets: [
                                {
                                  label: 'Equipamentos',
                                  data: equipmentMetrics.esmr.data,
                                  backgroundColor: createBarGradient(ctx, brandColors.chartColors.esmr, isDark),
                                  borderColor: isDark ? brandColors.chartColors.esmr.dark : brandColors.chartColors.esmr.light,
                                  borderWidth: 1,
                                  hoverBackgroundColor: isDark ? brandColors.chartColors.esmr.dark : brandColors.chartColors.esmr.light,
                                  hoverBorderColor: isDark ? '#ffffff' : '#000000',
                                  barPercentage: 0.7,
                                  categoryPercentage: 0.8
                                }
                              ]
                            };
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                
                <div className="d-flex mt-5 gap-2">
                  <Button
                    variant="outline-primary"
                    className="d-flex align-items-center"
                    href="/equipments"
                    style={{borderColor: brandColors.darkGreen, color: brandColors.darkGreen}}
                  >
                    <MdStorage className="me-1" /> Visualizar Equipamentos
                  </Button>

                  <Button
                    variant="outline-success"
                    className="d-flex align-items-center"
                    onClick={() => { window.open(process.env.REACT_APP_API_GLOBAL_SERVER + "/log", "_blank") }}
                    style={{borderColor: brandColors.lightGreen, color: brandColors.darkGreen}}
                  >
                    <MdOpenInNew className="me-1" /> Download Relatório
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Certificado Dashboard */}
            <Card className='mb-4 dashboard-card' style={{borderRadius: '16px'}}>
              <Card.Body className="p-4">
                <h4 className="mb-4 fw-bold section-header" style={{color: 'var(--brand-dark-green)'}}>Certificados</h4>
                <CertificadoDash brandColors={brandColors} />
              </Card.Body>
            </Card>
          </Container>
        </Page>
      ) : null}
    </>
  )
}

export default Home;
